/**
 * App Widget Persistence and Initialization
 * Handles widget reinitialization and cross-browser compatibility
 */
(function() {
  'use strict';
  
  // Note: Network interception code removed after debugging phase
  // We now have the correct endpoint and data format for Restock Alerts:
  // - Endpoint: https://alertmerestockalerts.herokuapp.com/addemail
  // - Format: JSON with email, store, product (numeric ID), variant, etc.
  // The manual button handlers in variant-widget-handler.js handle submissions directly.

  // Consolidated widget initialization - the single source of truth
  window.reinitializeAppWidgets = function() {
    console.log('Reinitializing all app widgets...');
    
    // Kite GWP - try all known initialization methods
    const kiteWrapper = document.querySelector('.gftFreeGiftWrapper');
    if (kiteWrapper) {
      console.log('Reinitializing Kite GWP widget...');
      if (window.KiteGWP && typeof window.KiteGWP.init === 'function') {
        window.KiteGWP.init();
      } else if (window.Kite2 && typeof window.Kite2.init === 'function') {
        window.Kite2.init();
      } else if (window.gftInit && typeof window.gftInit === 'function') {
        window.gftInit();
      } else {
        window.dispatchEvent(new CustomEvent('kite:gwp:init'));
      }
    }
    
    // Selleasy Upsell - try all known initialization methods
    const selleasyWidget = document.querySelector('lb-upsell-widget');
    if (selleasyWidget) {
      console.log('Reinitializing Selleasy widget...');
      if (window.LB && typeof window.LB.init === 'function') {
        window.LB.init();
      } else if (window.LBUpsell && typeof window.LBUpsell.init === 'function') {
        window.LBUpsell.init();
      } else if (window.Selleasy && typeof window.Selleasy.init === 'function') {
        window.Selleasy.init();
      } else if (window.Selleasy2 && typeof window.Selleasy2.init === 'function') {
        window.Selleasy2.init();
      } else {
        window.dispatchEvent(new CustomEvent('selleasy:init'));
        window.dispatchEvent(new CustomEvent('lb:upsell:init'));
      }
      
      // Custom element reinitialization
      if (typeof selleasyWidget.connectedCallback === 'function') {
        selleasyWidget.connectedCallback();
      }
    }
    
    // Yotpo widgets (if present)
    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
      console.log('Reinitializing Yotpo widgets...');
      window.yotpoWidgetsContainer.initWidgets();
    }
    
    // Alert Me! Restock Alerts - try global init first, then fallback to script re-execution
    const restockContainer = document.querySelector('.da-restock-alerts-container-v1');
    if (restockContainer) {
      console.log('Reinitializing Restock Alerts widget...');
      
      let reinitSuccess = false;
      
      // Try app's global initialization methods
      if (window.DARestockAlerts && typeof window.DARestockAlerts.init === 'function') {
        window.DARestockAlerts.init();
        reinitSuccess = true;
      } else if (window.AlertMe && typeof window.AlertMe.init === 'function') {
        window.AlertMe.init();
        reinitSuccess = true;
      } else if (window.RestockAlerts && typeof window.RestockAlerts.init === 'function') {
        window.RestockAlerts.init();
        reinitSuccess = true;
      }
      
      // If global init didn't work, try script re-execution
      if (!reinitSuccess) {
        console.log('Global init failed, trying script re-execution...');
        const scripts = restockContainer.querySelectorAll('script');
        scripts.forEach(function(script) {
          if (script.textContent && script.textContent.trim()) {
            try {
              const newScript = document.createElement('script');
              
              // Copy attributes
              for (let i = 0; i < script.attributes.length; i++) {
                const attr = script.attributes[i];
                newScript.setAttribute(attr.name, attr.value);
              }
              
              if (script.src) {
                newScript.src = script.src;
              } else {
                newScript.textContent = script.textContent;
              }
              
              script.parentNode.insertBefore(newScript, script.nextSibling);
              console.log('Re-executed restock alerts script via DOM insertion');
            } catch (e) {
              console.log('Script execution failed:', e);
            }
          }
        });
      }
      
      // Dispatch events as final fallback
      window.dispatchEvent(new CustomEvent('da:restock:init'));
      window.dispatchEvent(new CustomEvent('alertme:init'));
      window.dispatchEvent(new CustomEvent('restock:alerts:init'));
    }
    
    // Dispatch global event that all widgets are ready
    window.dispatchEvent(new CustomEvent('leatherman:widgets:ready'));
  };

  // Debounced version for MutationObserver to prevent excessive calls
  let widgetInitTimeout = null;
  function debouncedWidgetInit() {
    clearTimeout(widgetInitTimeout);
    widgetInitTimeout = setTimeout(window.reinitializeAppWidgets, 100);
  }

  // MutationObserver to detect when widgets are added dynamically
  const widgetObserver = new MutationObserver(function(mutations) {
    let shouldReinit = false;
    
    mutations.forEach(function(mutation) {
      mutation.addedNodes.forEach(function(node) {
        if (node.nodeType === 1) { // Element node
          // Check for any app widget containers
          if (node.classList && (
              node.classList.contains('da-restock-alerts-container-v1') ||
              node.classList.contains('gftFreeGiftWrapper') ||
              node.tagName === 'LB-UPSELL-WIDGET'
          )) {
            shouldReinit = true;
          }
          // Check for nested widget containers
          else if (node.querySelector && (
              node.querySelector('.da-restock-alerts-container-v1') ||
              node.querySelector('.gftFreeGiftWrapper') ||
              node.querySelector('lb-upsell-widget')
          )) {
            shouldReinit = true;
          }
        }
      });
    });
    
    if (shouldReinit) {
      debouncedWidgetInit();
    }
  });

  // Function to add fallback classes for browsers without :has() support
  function addWidgetFallbackClasses() {
    // Check if browser supports :has() pseudo-class - handle cases where CSS.supports is undefined
    const supportsHas = (typeof CSS !== 'undefined' && typeof CSS.supports === 'function')
      ? CSS.supports('selector(:has(*))')
      : false;
    
    if (!supportsHas) {
      // Add fallback classes for older browsers
      document.querySelectorAll('.product-info__block').forEach(function(block) {
        if (block.querySelector('.gftFreeGiftWrapper')) {
          block.classList.add('has-gftFreeGiftWrapper');
        }
        if (block.querySelector('lb-upsell-widget')) {
          block.classList.add('has-lb-upsell-widget');
        }
        if (block.querySelector('.da-restock-alerts-container-v1')) {
          block.classList.add('has-da-restock-alerts-container-v1');
        }
      });
    }
  }
  
  // Start observing once DOM is ready - scope to product section for better performance
  function startWidgetObserver() {
    // Only observe the product section to reduce noise
    const productSection = document.querySelector('.js-product') || 
                          document.querySelector('[data-section]') ||
                          document.body; // fallback to body if no product section found
    
    widgetObserver.observe(productSection, {
      childList: true,
      subtree: true
    });
    addWidgetFallbackClasses();
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startWidgetObserver);
  } else {
    startWidgetObserver();
  }
  
  // Also add fallback classes when widgets are dynamically added
  window.addEventListener('shopify:section:reloaded', function() {
    setTimeout(addWidgetFallbackClasses, 100);
  });
})();